// src/pages/couriers/components/RuleModal.jsx
import React from 'react';
import { X, Plus, Edit, RefreshCw, Save, Trash2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter';
import styles from './RuleModal.module.css';

const RuleModal = ({
  isOpen,
  onClose,
  editingRule,
  ruleForm,
  setRuleForm,
  businessCouriers,
  cities,
  districts,
  provinces,
  shippingZones,
  shops,
  onSave,
  isSubmitting
}) => {
  const { t } = useLocalization();

  if (!isOpen) return null;

  const addRuleEntry = (ruleType) => {
    setRuleForm(prev => ({
      ...prev,
      rules: {
        ...prev.rules,
        [ruleType]: [
          ...(prev.rules[ruleType] || []),
          { type: 'include', [getRuleIdField(ruleType)]: [] }
        ]
      }
    }));
  };

  const removeRuleEntry = (ruleType, index) => {
    setRuleForm(prev => ({
      ...prev,
      rules: {
        ...prev.rules,
        [ruleType]: (prev.rules[ruleType] || []).filter((_, i) => i !== index)
      }
    }));
  };

  const updateRuleEntry = (ruleType, index, field, value) => {
    setRuleForm(prev => ({
      ...prev,
      rules: {
        ...prev.rules,
        [ruleType]: (prev.rules[ruleType] || []).map((entry, i) =>
          i === index ? { ...entry, [field]: value } : entry
        )
      }
    }));
  };

  const getRuleIdField = (ruleType) => {
    const fieldMap = {
      cityRules: 'cityIds',
      districtRules: 'districtIds',
      provinceRules: 'provinceIds',
      shippingZoneRules: 'shippingZoneIds',
      shopRules: 'shopIds'
    };
    return fieldMap[ruleType];
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.ruleModal}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>
            {editingRule ? <Edit size={20} /> : <Plus size={20} />}
            {editingRule ? t('couriers.editRule') : t('couriers.createRuleTitle')}
          </h2>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.formSection}>
            <h3>{t('couriers.basicInformation')}</h3>
            <div className={styles.formGroup}>
              <label>{t('couriers.courierCompany')} *</label>
              <select
                value={ruleForm.businessCourierId}
                onChange={(e) => setRuleForm({ ...ruleForm, businessCourierId: parseInt(e.target.value) })}
                required
              >
                <option value="">{t('couriers.selectCourierCompany')}</option>
                {businessCouriers
                  .filter(bc => bc.isActive)
                  .map(bc => (
                    <option key={bc.id} value={bc.id}>
                      {bc?.accountName}
                    </option>
                  ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>{t('couriers.ruleName')} *</label>
              <input
                type="text"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                placeholder={t('couriers.ruleNamePlaceholder')}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>{t('couriers.description')}</label>
              <textarea
                value={ruleForm.description}
                onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                placeholder={t('couriers.descriptionPlaceholder')}
                rows={3}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.toggleLabel}>
                <span>{t('couriers.activeStatusLabel')}</span>
                <div className={styles.toggleWrapper}>
                  <input
                    type="checkbox"
                    checked={ruleForm.isActive}
                    onChange={(e) => setRuleForm({ ...ruleForm, isActive: e.target.checked })}
                    className={styles.toggleInput}
                  />
                  <span className={styles.toggleSlider}></span>
                </div>
              </label>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.toggleLabel}>
                <div className={styles.toggleLabelContent}>
                  <span>{t('couriers.setAsDefault')}</span>
                  <small className={styles.toggleHint}>{t('couriers.setAsDefaultHint')}</small>
                </div>
                <div className={styles.toggleWrapper}>
                  <input
                    type="checkbox"
                    checked={ruleForm.isDefault}
                    onChange={(e) => setRuleForm({ ...ruleForm, isDefault: e.target.checked })}
                    className={styles.toggleInput}
                  />
                  <span className={styles.toggleSlider}></span>
                </div>
              </label>
            </div>
          </div>
          <div className={styles.formSection}>
            <h3>{t('couriers.ruleConfiguration')}</h3>
            <p className={styles.sectionDescription}>
              {t('couriers.ruleConfigDescription')}
            </p>
            <RuleTypeSection
              title={t('couriers.shopRules')}
              ruleType="shopRules"
              ruleForm={ruleForm}
              availableItems={shops}
              itemLabelField="name"
              addRuleEntry={addRuleEntry}
              removeRuleEntry={removeRuleEntry}
              updateRuleEntry={updateRuleEntry}
              getRuleIdField={getRuleIdField}
            />
            <RuleTypeSection
              title={t('couriers.cityRules')}
              ruleType="cityRules"
              ruleForm={ruleForm}
              availableItems={cities}
              itemLabelField="name"
              addRuleEntry={addRuleEntry}
              removeRuleEntry={removeRuleEntry}
              updateRuleEntry={updateRuleEntry}
              getRuleIdField={getRuleIdField}
            />
            <RuleTypeSection
              title={t('couriers.shippingZoneRules')}
              ruleType="shippingZoneRules"
              ruleForm={ruleForm}
              availableItems={shippingZones}
              itemLabelField="name"
              addRuleEntry={addRuleEntry}
              removeRuleEntry={removeRuleEntry}
              updateRuleEntry={updateRuleEntry}
              getRuleIdField={getRuleIdField}
            />
            <RuleTypeSection
              title={t('couriers.districtRules')}
              ruleType="districtRules"
              ruleForm={ruleForm}
              availableItems={districts}
              itemLabelField="name"
              addRuleEntry={addRuleEntry}
              removeRuleEntry={removeRuleEntry}
              updateRuleEntry={updateRuleEntry}
              getRuleIdField={getRuleIdField}
            />
            <RuleTypeSection
              title={t('couriers.provinceRules')}
              ruleType="provinceRules"
              ruleForm={ruleForm}
              availableItems={provinces}
              itemLabelField="name"
              addRuleEntry={addRuleEntry}
              removeRuleEntry={removeRuleEntry}
              updateRuleEntry={updateRuleEntry}
              getRuleIdField={getRuleIdField}
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.secondaryButton} disabled={isSubmitting}>
            {t('couriers.cancel')}
          </button>
          <button
            onClick={onSave}
            className={styles.primaryButton}
            disabled={isSubmitting || !ruleForm.businessCourierId || !ruleForm.name}
          >
            {isSubmitting ? (
              <>
                <RefreshCw size={16} className={styles.spinning} />
                {t('couriers.saving')}
              </>
            ) : (
              <>
                <Save size={16} />
                {editingRule ? t('couriers.updateRule') : t('couriers.createRule')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const RuleTypeSection = ({
  title,
  ruleType,
  ruleForm,
  availableItems,
  itemLabelField,
  addRuleEntry,
  removeRuleEntry,
  updateRuleEntry,
  getRuleIdField
}) => {
  const { t } = useLocalization();
  const rules = ruleForm.rules[ruleType] || [];
  const idField = getRuleIdField(ruleType);

  const selectOptions = availableItems.map(item => ({
    key: item.id,
    label: item[itemLabelField]
  }));

  return (
    <div className={styles.ruleTypeSection}>
      <div className={styles.ruleTypeHeader}>
        <h4>{title}</h4>
        <button type="button" onClick={() => addRuleEntry(ruleType)} className={styles.addRuleButton}>
          <Plus size={14} />
          {t('couriers.addRule')}
        </button>
      </div>
      {rules.length === 0 ? (
        <p className={styles.noRulesText}>{t('couriers.noRulesConfigured')}</p>
      ) : (
        <div className={styles.ruleEntriesList}>
          {rules.map((entry, index) => (
            <div key={index} className={styles.ruleEntry}>
              <div className={styles.ruleEntryHeader}>
                <select
                  value={entry.type}
                  onChange={(e) => updateRuleEntry(ruleType, index, 'type', e.target.value)}
                  className={`${styles.ruleTypeSelect} ${
                    entry.type === 'include' ? styles.includeSelect : styles.excludeSelect
                  }`}
                >
                  <option value="include">{t('couriers.include')}</option>
                  <option value="exclude">{t('couriers.exclude')}</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeRuleEntry(ruleType, index)}
                  className={styles.removeEntryButton}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              
              <div className={styles.itemSelection}>
                <MultiSelectFilter
                  options={selectOptions}
                  selectedValues={entry[idField] || []}
                  onChange={(selectedIds) => {
                    updateRuleEntry(ruleType, index, idField, selectedIds);
                  }}
                  placeholder={`${t('couriers.selectCourierCompany')} ${title.toLowerCase().replace(' rules', '')}...`}
                  searchPlaceholder={`${t('couriers.searchRules')} ${title.toLowerCase().replace(' rules', '')}...`}
                  label={entry.type === 'include' ? t('couriers.selectItemsToInclude') : t('couriers.selectItemsToExclude')}
                  displayKey="label"
                  valueKey="key"
                  searchKey="label"
                />
              </div>

              {entry[idField]?.length > 0 && (
                <div className={styles.selectedItems}>
                  <strong>{t('couriers.selected')} ({entry[idField].length}):</strong>
                  <div className={styles.selectedItemsList}>
                    {entry[idField].map(id => {
                      const item = availableItems.find(i => i.id === id);
                      return item ? (
                        <span key={id} className={styles.selectedItemTag}>
                          {item[itemLabelField]}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RuleModal;